import { filter, find, map } from 'lodash-es'
import { FC, useCallback, useState } from 'react'
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form'

import { GroupCurrency } from './GroupCurrency'
import { GroupFestival } from './GroupFestival'
import { TFormValues } from './GroupsTab'
import Loader from '../../../common/components/Loader'
import { Currency } from '../../../common/models'
import { IGroup, PaymentProvider } from '../../../common/models/groups'
import FormGroup from '../../../core/components/FormGroup'
import {
  createGroup,
  deleteGroup,
  updateGroup,
  updateGroupCurrencyDetails,
} from '../../../core/services/groups'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

type Props = {
  group: FieldArrayWithId<TFormValues, 'groups'>
  control: Control<TFormValues>
  errors: FieldErrors<TFormValues>
  register: UseFormRegister<TFormValues>
  currencies: Currency[]
  index: number
  remove: UseFieldArrayRemove
  watch: UseFormWatch<TFormValues>
  setValue: UseFormSetValue<TFormValues>
  trigger: UseFormTrigger<TFormValues>
  groups: IGroup[]
  paymentProviders: PaymentProvider[]
}

export const Group: FC<Props> = ({
  groups,
  errors,
  register,
  control,
  index,
  remove,
  currencies,
  watch,
  setValue,
  trigger,
  paymentProviders,
}): JSX.Element => {
  const {
    fields,
    append,
    remove: removeCurrency,
  } = useFieldArray({
    control: control,
    name: `groups.${index}.currencies`,
  })

  const [savingName, setSavingName] = useState(false)

  const updateName = useCallback(async () => {
    const valid = await trigger(`groups.${index}.name`)
    if (!valid) {
      return
    }
    const name = watch(`groups.${index}.name`)
    const id = watch(`groups.${index}.id`)
    if (!name) {
      return
    }
    if (id) {
      try {
        setSavingName(true)
        const { data, status } = await updateGroup(id, name)
        if (status === 200) {
          setValue(`groups.${index}.name`, data.name)
          setValue(`groups.${index}.id`, data.id)
          setValue(`groups.${index}.saved`, true)
        }
      } catch (err) {
        console.log(err)
      } finally {
        setSavingName(false)
      }
    } else {
      try {
        setSavingName(true)
        const { data, status } = await createGroup(name)
        if (status === 200) {
          setValue(`groups.${index}.name`, data.name)
          setValue(`groups.${index}.id`, data.id)
          setValue(`groups.${index}.saved`, true)
        }
      } catch (err) {
        console.log(err)
      } finally {
        setSavingName(false)
      }
    }
  }, [])

  const deleteGroupItem = useCallback(async () => {
    try {
      setSaving(true)
      const id = watch(`groups.${index}.id`)
      if (id) {
        const { status } = await deleteGroup(id)
        if (status === 204) {
          setDeleteConfirmation(false)
          remove(index)
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setSaving(false)
    }
  }, [])

  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)

  const [deleteIndex, setDeleteIndex] = useState<number | undefined>()

  const currenciesList = watch(`groups.${index}.currencies`)

  const {
    fields: fieldsFestivals,
    append: appendFestivals,
    remove: removeFestivals,
  } = useFieldArray({
    control: control,
    name: `groups.${index}.festivals`,
  })

  const mainCurrency = find(currenciesList, { main: true })
  const restCurrencies = filter(currenciesList, (c) => c.main !== true)

  const [saving, setSaving] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const updateCurrencyDetails = useCallback(async () => {
    const valid = await trigger(`groups.${index}.currencies`)
    if (!valid) return
    try {
      const groupId = watch(`groups.${index}.id`)
      const currencies = watch(`groups.${index}.currencies`)
      if (!groupId || !currencies) return
      setSaving(true)
      await updateGroupCurrencyDetails(
        groupId,
        map(currencies, (c) => ({
          id: c?.id || '',
          name: c.name,
          active: c.active ? true : false,
          exchangeRate: c.conversionRate as number,
          default: c.main ? true : false,
        })),
      )
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSaving(false)
    }
  }, [])

  const groupName = groups?.[index]?.name || watch(`groups.${index}.name`)

  return (
    <>
      {(savingName || saving) && <Loader />}
      <div className="p-4 mb-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-title-s text-accent-1">{groupName}</h2>
          <Button
            variant="destructive"
            type="button"
            onClick={() => {
              setDeleteIndex(index)
              setDeleteConfirmation(true)
            }}
          >
            Delete Group
            <Icon
              icon="close-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
          </Button>
        </div>
        <div className="flex w-full gap-2 mb-4">
          <FormGroup label="Group Name" wrapperClassName="mb-0 w-full">
            <Input
              isInvalid={!!errors?.groups?.[index]?.name?.message}
              {...register(`groups.${index}.name`)}
              type="text"
              placeholder="Group Name"
            />
          </FormGroup>
          <div className="flex gap-2 items-end [&>*]:w-[154px]">
            <Button
              variant="secondary"
              className="flex items-center justify-center"
              type="button"
              onClick={() => {
                setValue(`groups.${index}.name`, groups?.[index]?.name, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              className="flex items-center justify-center"
              onClick={updateName}
            >
              Save
            </Button>
          </div>
        </div>
        {map(fields, (field, ind) => (
          <GroupCurrency
            index={ind}
            groupIndex={index}
            remove={removeCurrency}
            currencies={currencies}
            key={field.id}
            currency={field}
            control={control}
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            trigger={trigger}
          />
        ))}
        <div className="w-full flex items-center justify-center mt-4">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              append({
                id: '',
                name: '',
                symbol: '',
                alignment: 'BEFORE_PRICE',
              })
            }}
          >
            Add new Currency
            <Icon
              icon="plus"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
          </Button>
        </div>
        {currenciesList && currenciesList?.length > 0 && (
          <Card className="py-4 px-4 mt-4 bg-monochrome-3-60 border border-monochrome-120">
            <h4 className="mb-2 text-title-xs text-monochrome-1">
              Select Active Currencies
            </h4>
            <div className="grid grid-cols-4 divide-x divide-monochrome-120 gap-4 border-b border-monochrome-120 pb-4 mb-4 mt-4">
              {map(currenciesList, (currency, ind) => (
                <Controller
                  key={`active-${ind}`}
                  name={`groups.${index}.currencies.${ind}.active`}
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label={currency.name}
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                      direction="row"
                      labelProps={{
                        htmlFor: `active-${ind}`,
                      }}
                    >
                      <Checkbox
                        id={`active-${ind}`}
                        onCheckedChange={field.onChange}
                        checked={field.value || false}
                      />
                    </FormGroup>
                  )}
                />
              ))}
            </div>
            <h4 className="mb-2 text-title-xs text-monochrome-1">
              Select Main Currency
            </h4>
            <div className="grid grid-cols-4 divide-x divide-monochrome-120 gap-4 border-b border-monochrome-120 pb-4 mb-4 mt-4">
              {map(currenciesList, (currency, ind) => (
                <Controller
                  key={`main-${ind}`}
                  name={`groups.${index}.currencies.${ind}.main`}
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label={currency.name}
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                      direction="row"
                      labelProps={{
                        htmlFor: `main-${ind}`,
                      }}
                    >
                      <Checkbox
                        id={`main-${ind}`}
                        onCheckedChange={(checked) => {
                          currenciesList?.forEach((_, i) => {
                            setValue(
                              `groups.${index}.currencies.${i}.main`,
                              i === ind ? checked === true : false,
                            )
                          })
                        }}
                        checked={field.value || false}
                      />
                    </FormGroup>
                  )}
                />
              ))}
            </div>
            {mainCurrency && restCurrencies.length > 0 && (
              <>
                <h3 className="mt-4 text-title-xs text-monochrome-1">
                  Matrix Conversion Rate
                </h3>
                <div className="py-4 mt-2">
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                      <p className="text-title-xxs">{mainCurrency?.name}</p>
                      <span className="text-button-m text-monochrome-2">
                        Festival Group Main Currency
                      </span>
                    </div>
                    <div className="flex justify-between flex-grow items-center gap-2">
                      <span className="mt-4">=</span>
                      {map(currenciesList, (currency, ind) => {
                        if (!currency.main) {
                          return (
                            <FormGroup
                              key={`conv-${ind}`}
                              label={currency.name}
                            >
                              <Input
                                type="number"
                                placeholder="Enter Conversion Rate"
                                {...register(
                                  `groups.${index}.currencies.${ind}.conversionRate`,
                                )}
                              />
                            </FormGroup>
                          )
                        }
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="flex gap-2 justify-end mt-4 border-t border-monochrome-120 pt-4 [&>*]:w-[154px]">
              <Button
                variant="secondary"
                className="flex items-center justify-center"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                className="flex items-center justify-center"
                onClick={updateCurrencyDetails}
              >
                Save
              </Button>
            </div>
          </Card>
        )}
        {fieldsFestivals.length > 0 && (
          <h2 className="text-title-xs text-monochrome-1 my-4">Festivals</h2>
        )}
        {map(fieldsFestivals, (field, ind) => (
          <GroupFestival
            index={ind}
            groupIndex={index}
            remove={removeFestivals}
            key={field.id}
            control={control}
            register={register}
            errors={errors}
            festival={field}
            watch={watch}
            currenciesList={currenciesList}
            setValue={setValue}
            trigger={trigger}
            paymentProviders={paymentProviders}
          />
        ))}
        <div className="w-full flex items-center justify-center mt-4">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              appendFestivals({
                id: '',
                saved: false,
                currencies: map(currenciesList, (c) => ({
                  id: c.id,
                  name: c.name,
                  symbol: c.symbol,
                  alignment: c.alignment,
                  active: false,
                  default: false,
                })),
                defaultPaymentProviderId: '',
              })
            }}
          >
            Add new Festival
            <Icon
              icon="plus"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
          </Button>
        </div>
        <DeleteConfirmationModal
          showDeleteConfirmationModal={deleteConfirmation}
          setDeleteConfirmationModal={setDeleteConfirmation}
          deleteTitle={'Group'}
          deleteFunc={() => {
            const saved = watch(`groups.${index}.saved`)
            if (saved) {
              deleteGroupItem()
            } else {
              remove(deleteIndex as number)
              setDeleteConfirmation(false)
            }
          }}
        />
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
